// Product media, sharing, and single-option variant purchasing interactions.
class ProductExperience extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.setupGallery();
    this.setupNativeSharing();
    this.setupPurchaseMatrix();
  }

  setupGallery() {
    const gallery = this.querySelector('[data-product-gallery]');
    if (!gallery) return;

    const frames = Array.from(gallery.querySelectorAll('[data-product-media]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-media-thumb]'));
    const thumbCarousel = gallery.querySelector('[data-product-thumb-carousel]');
    const thumbTrack = gallery.querySelector('[data-product-thumb-track]');
    const previousThumbs = gallery.querySelector('[data-product-thumb-previous]');
    const nextThumbs = gallery.querySelector('[data-product-thumb-next]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (thumbCarousel && thumbTrack && previousThumbs && nextThumbs) {
      let scrollFrame;

      const edgeTolerance = 6;
      const atStart = () => thumbTrack.scrollLeft <= edgeTolerance;
      const atEnd = () => thumbTrack.scrollLeft + thumbTrack.clientWidth >= thumbTrack.scrollWidth - edgeTolerance;
      const hasOverflow = () => thumbTrack.scrollWidth > thumbTrack.clientWidth + 2;

      const updateThumbControls = () => {
        const overflowing = hasOverflow();
        thumbCarousel.classList.toggle('is-overflowing', overflowing);
        previousThumbs.hidden = !overflowing || atStart();
        nextThumbs.hidden = !overflowing || atEnd();
      };

      const scrollDistance = () => {
        const firstThumb = thumbs[0];
        if (!firstThumb) return thumbTrack.clientWidth;

        const styles = getComputedStyle(thumbTrack);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
        const itemWidth = firstThumb.getBoundingClientRect().width + gap;
        const itemsPerPage = Math.max(1, Math.floor((thumbTrack.clientWidth + gap) / itemWidth));
        return itemWidth * itemsPerPage;
      };

      const scrollThumbs = (direction) => {
        thumbTrack.scrollBy({
          left: scrollDistance() * direction,
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
        });
      };

      previousThumbs.addEventListener('click', () => scrollThumbs(-1));
      nextThumbs.addEventListener('click', () => scrollThumbs(1));
      thumbTrack.addEventListener('scroll', () => {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(updateThumbControls);
      }, { passive: true });

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(updateThumbControls);
        resizeObserver.observe(thumbTrack);
      } else {
        window.addEventListener('resize', updateThumbControls);
      }

      thumbCarousel.classList.add('is-enhanced');
      updateThumbControls();
    }

    const activateMedia = (mediaId) => {
      frames.forEach((frame) => {
        const active = frame.dataset.productMedia === mediaId;
        frame.hidden = !active;
        frame.classList.toggle('is-active', active);
        frame.setAttribute('aria-hidden', String(!active));

        if (!active) frame.querySelectorAll('video').forEach((video) => video.pause());
      });

      thumbs.forEach((thumb) => {
        const active = thumb.dataset.mediaThumb === mediaId;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
      });
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => activateMedia(thumb.dataset.mediaThumb));
    });

    frames.forEach((frame) => {
      if (frame.dataset.mediaType !== 'image') return;
      frame.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'touch') return;
        const bounds = frame.getBoundingClientRect();
        const x = ((event.clientX - bounds.left) / bounds.width) * 100;
        const y = ((event.clientY - bounds.top) / bounds.height) * 100;
        frame.style.setProperty('--zoom-x', `${x}%`);
        frame.style.setProperty('--zoom-y', `${y}%`);
      });
    });
  }

  setupNativeSharing() {
    this.querySelectorAll('[data-native-share]').forEach((button) => {
      button.addEventListener('click', async () => {
        const shareData = {
          title: button.dataset.shareTitle,
          url: button.dataset.shareUrl,
        };

        if (navigator.share) {
          try {
            await navigator.share(shareData);
          } catch (error) {
            if (error.name !== 'AbortError') console.error(error);
          }
          return;
        }

        try {
          await navigator.clipboard.writeText(shareData.url);
          const originalLabel = button.textContent;
          const matrix = this.querySelector('[data-purchase-matrix]');
          button.textContent = matrix?.dataset.labelShareCopied || originalLabel;
          window.setTimeout(() => {
            button.textContent = originalLabel;
          }, 1800);
        } catch (_error) {
          window.prompt('Copy this link', shareData.url);
        }
      });
    });
  }

  setupPurchaseMatrix() {
    const matrix = this.querySelector('[data-purchase-matrix]');
    if (!matrix) return;

    const rowsContainer = matrix.querySelector('[data-purchase-rows]');
    const rowTemplate = matrix.querySelector('[data-purchase-row-template]');
    const picker = matrix.querySelector('[data-variant-picker]');
    const pickerToggle = matrix.querySelector('[data-variant-picker-toggle]');
    const pickerMenu = matrix.querySelector('[data-variant-picker-menu]');
    const pickerChoices = matrix.querySelector('.variant-picker__choices');
    const choices = Array.from(matrix.querySelectorAll('[data-variant-choice]'));
    const emptyState = matrix.querySelector('[data-purchase-empty]');
    if (!rowsContainer || !rowTemplate || !picker || !pickerToggle || !pickerMenu || !pickerChoices) return;

    const selectedVariantIds = new Set();
    const lowStockThreshold = Number(matrix.dataset.lowStockThreshold || 10);
    const checkoutEnabled = matrix.dataset.checkoutEnabled === 'true';
    const rootUrl = matrix.dataset.routesRoot.endsWith('/')
      ? matrix.dataset.routesRoot
      : `${matrix.dataset.routesRoot}/`;

    let moneyFormatter;
    try {
      moneyFormatter = new Intl.NumberFormat(matrix.dataset.locale || undefined, {
        style: 'currency',
        currency: matrix.dataset.currency,
      });
    } catch (_error) {
      moneyFormatter = null;
    }

    const formatMoney = (cents) => {
      const amount = Number(cents || 0) / 100;
      return moneyFormatter
        ? moneyFormatter.format(amount)
        : `${matrix.dataset.currency} ${amount.toFixed(2)}`;
    };

    const getRows = () => Array.from(rowsContainer.querySelectorAll('[data-purchase-row]'));

    const variantFor = (row) => ({
      id: row.dataset.variantId,
      price: Number(row.dataset.variantPrice || 0),
      available: row.dataset.variantAvailable === 'true',
      inventoryQuantity: Number(row.dataset.inventoryQuantity || 0),
      inventoryTracked: Boolean(row.dataset.inventoryManagement),
      inventoryPolicy: row.dataset.inventoryPolicy,
    });

    const statusFor = (variant) => {
      if (!variant.available) {
        return { label: matrix.dataset.labelSoldOut, state: 'sold-out' };
      }
      if (
        variant.inventoryTracked &&
        variant.inventoryPolicy !== 'continue' &&
        variant.inventoryQuantity > 0 &&
        variant.inventoryQuantity <= lowStockThreshold
      ) {
        return { label: matrix.dataset.labelLowStock, state: 'low-stock' };
      }
      return { label: matrix.dataset.labelInStock, state: 'in-stock' };
    };

    const quantityFor = (row) => Number(row.querySelector('[data-row-quantity]').value || 1);

    const closePicker = () => {
      pickerMenu.hidden = true;
      pickerMenu.style.visibility = '';
      pickerToggle.setAttribute('aria-expanded', 'false');
    };

    const positionPicker = () => {
      if (pickerMenu.hidden) return;

      const viewport = window.visualViewport;
      const viewportLeft = viewport?.offsetLeft || 0;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportWidth = viewport?.width || document.documentElement.clientWidth;
      const viewportHeight = viewport?.height || window.innerHeight;
      const viewportRight = viewportLeft + viewportWidth;
      const viewportBottom = viewportTop + viewportHeight;
      const safeGutter = 16;
      const menuGap = 8;
      const buttonRect = pickerToggle.getBoundingClientRect();

      if (buttonRect.bottom < viewportTop || buttonRect.top > viewportBottom) {
        closePicker();
        return;
      }

      pickerMenu.style.maxWidth = `${Math.floor(Math.max(0, viewportWidth - safeGutter * 2))}px`;
      pickerMenu.style.maxHeight = '';
      pickerMenu.style.removeProperty('--variant-picker-choices-max-height');

      const naturalMenuRect = pickerMenu.getBoundingClientRect();
      const naturalChoicesHeight = pickerChoices.getBoundingClientRect().height;
      const menuChromeHeight = Math.max(0, naturalMenuRect.height - naturalChoicesHeight);
      const spaceBelow = Math.max(0, viewportBottom - safeGutter - buttonRect.bottom - menuGap);
      const spaceAbove = Math.max(0, buttonRect.top - viewportTop - safeGutter - menuGap);
      const opensAbove = naturalMenuRect.height > spaceBelow && spaceAbove > spaceBelow;
      const availableHeight = Math.floor(opensAbove ? spaceAbove : spaceBelow);

      pickerMenu.style.maxHeight = `${availableHeight}px`;
      pickerMenu.style.setProperty(
        '--variant-picker-choices-max-height',
        `${Math.max(0, availableHeight - menuChromeHeight)}px`,
      );

      const menuRect = pickerMenu.getBoundingClientRect();
      const buttonCenter = buttonRect.left + buttonRect.width / 2;
      const relativeButtonCenter = (buttonCenter - viewportLeft) / viewportWidth;
      let left;

      if (relativeButtonCenter >= 0.66) {
        left = buttonRect.right - menuRect.width;
      } else if (relativeButtonCenter <= 0.34) {
        left = buttonRect.left;
      } else {
        left = buttonCenter - menuRect.width / 2;
      }

      left = Math.min(
        Math.max(left, viewportLeft + safeGutter),
        viewportRight - safeGutter - menuRect.width,
      );

      let top = opensAbove
        ? buttonRect.top - menuGap - menuRect.height
        : buttonRect.bottom + menuGap;
      top = Math.min(
        Math.max(top, viewportTop + safeGutter),
        viewportBottom - safeGutter - menuRect.height,
      );

      pickerMenu.style.left = `${Math.floor(left)}px`;
      pickerMenu.style.top = `${Math.floor(top)}px`;
      pickerMenu.dataset.placement = opensAbove ? 'top' : 'bottom';
    };

    let pickerPositionFrame;
    const schedulePickerPosition = () => {
      if (pickerMenu.hidden) return;
      window.cancelAnimationFrame(pickerPositionFrame);
      pickerPositionFrame = window.requestAnimationFrame(positionPicker);
    };

    const openPicker = () => {
      pickerMenu.style.visibility = 'hidden';
      pickerMenu.hidden = false;
      pickerToggle.setAttribute('aria-expanded', 'true');
      positionPicker();
      pickerMenu.style.visibility = 'visible';
    };

    const updatePickerState = () => {
      const hasAvailableChoice = choices.some(
        (choice) => choice.dataset.variantAvailable === 'true' && !selectedVariantIds.has(choice.dataset.variantId),
      );
      pickerToggle.disabled = !hasAvailableChoice;
      if (!hasAvailableChoice) closePicker();
    };

    const updateChoiceState = (variantId, selected) => {
      const choice = choices.find((item) => item.dataset.variantId === variantId);
      if (!choice) return;
      choice.hidden = selected;
      choice.disabled = selected || choice.dataset.variantAvailable !== 'true';
    };

    const updateSummary = () => {
      let totalItems = 0;
      let subtotal = 0;
      let firstSelectedVariant;

      const rows = getRows();
      rows.forEach((row) => {
        const variant = variantFor(row);
        const quantity = quantityFor(row);
        if (!variant.available || quantity <= 0) return;
        firstSelectedVariant ||= variant;
        totalItems += quantity;
        subtotal += variant.price * quantity;
      });

      matrix.querySelector('[data-matrix-subtotal]').textContent = formatMoney(subtotal);

      const addButton = matrix.querySelector('[data-matrix-add-cart]');
      const buyButton = matrix.querySelector('[data-matrix-buy-now]');
      const isEmpty = rows.length === 0;
      addButton.disabled = totalItems === 0;
      buyButton.disabled = !checkoutEnabled || totalItems === 0;
      buyButton.setAttribute('aria-disabled', String(buyButton.disabled));
      emptyState.hidden = !isEmpty;
      matrix.classList.toggle('purchase-matrix--empty', isEmpty);
      updatePickerState();

      const mainPrice = this.querySelector('[data-main-product-price]');
      if (mainPrice && firstSelectedVariant) mainPrice.textContent = formatMoney(firstSelectedVariant.price);
    };

    const updateRow = (row) => {
      const variant = variantFor(row);
      const input = row.querySelector('[data-row-quantity]');
      const maximum = Math.max(1, Number(input.max || 999));
      const quantity = variant.available
        ? Math.max(1, Math.min(maximum, Number(input.value || 1)))
        : 1;
      input.value = String(quantity);

      const status = statusFor(variant);
      const availability = row.querySelector('[data-row-availability]');
      availability.textContent = status.label;
      availability.dataset.state = status.state;
      row.querySelector('[data-row-total]').textContent = formatMoney(variant.price * quantity);

      row.querySelector('[data-quantity-minus]').disabled = quantity <= 1 || !variant.available;
      row.querySelector('[data-quantity-plus]').disabled = quantity >= maximum || !variant.available;
      updateSummary();
    };

    const addVariant = (choice) => {
      const variantId = choice.dataset.variantId;
      if (selectedVariantIds.has(variantId) || choice.dataset.variantAvailable !== 'true') return;

      const row = rowTemplate.content.firstElementChild.cloneNode(true);
      const label = choice.dataset.variantLabel;
      const sku = choice.dataset.variantSku;
      const maximum = Math.max(1, Number(choice.dataset.maximumQuantity || 999));

      row.dataset.variantId = variantId;
      row.dataset.variantPrice = choice.dataset.variantPrice;
      row.dataset.variantAvailable = choice.dataset.variantAvailable;
      row.dataset.inventoryQuantity = choice.dataset.inventoryQuantity;
      row.dataset.inventoryManagement = choice.dataset.inventoryManagement;
      row.dataset.inventoryPolicy = choice.dataset.inventoryPolicy;
      row.querySelector('[data-row-variant-label]').textContent = label;

      const skuNode = row.querySelector('[data-row-sku]');
      skuNode.textContent = sku;
      skuNode.hidden = !sku;
      row.querySelector('[data-row-unit-price]').textContent = formatMoney(choice.dataset.variantPrice);

      const quantityInput = row.querySelector('[data-row-quantity]');
      quantityInput.max = String(maximum);
      quantityInput.value = '1';

      const quantityLabel = row.querySelector('[data-row-quantity-label]');
      quantityLabel.setAttribute('aria-label', `${quantityLabel.getAttribute('aria-label')}: ${label}`);
      const removeLabel = row.querySelector('[data-row-remove-label]');
      removeLabel.textContent = `${removeLabel.textContent}: ${label}`;

      rowsContainer.append(row);
      selectedVariantIds.add(variantId);
      updateChoiceState(variantId, true);
      updateRow(row);
      closePicker();
    };

    const removeRow = (row) => {
      const variantId = row.dataset.variantId;
      selectedVariantIds.delete(variantId);
      updateChoiceState(variantId, false);
      row.remove();
      updateSummary();
    };

    const collectItems = () =>
      getRows()
        .map((row) => ({ id: variantFor(row).id, quantity: quantityFor(row) }))
        .filter((item) => item.quantity > 0);

    const setBusy = (busy) => {
      const buttons = [
        matrix.querySelector('[data-matrix-add-cart]'),
        matrix.querySelector('[data-matrix-buy-now]'),
      ];
      buttons.forEach((button) => {
        button.dataset.defaultLabel ||= button.textContent.trim();
        button.textContent = busy ? matrix.dataset.labelLoading : button.dataset.defaultLabel;
        if (busy) button.disabled = true;
      });
      matrix.setAttribute('aria-busy', String(busy));
      if (!busy) updateSummary();
    };

    const submitItems = async (checkout) => {
      const items = collectItems();
      if (!items.length) return;

      const message = matrix.querySelector('[data-matrix-message]');
      message.hidden = true;
      setBusy(true);

      try {
        const response = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        });
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload.description || payload.message || 'Unable to add these items.');
        }

        message.textContent = matrix.dataset.labelAdded;
        message.hidden = false;
        window.location.assign(checkout ? matrix.dataset.checkoutUrl : matrix.dataset.cartUrl);
      } catch (error) {
        message.textContent = error.message;
        message.hidden = false;
        setBusy(false);
      }
    };

    updateSummary();

    matrix.addEventListener('input', (event) => {
      if (!event.target.matches('[data-row-quantity]')) return;
      const row = event.target.closest('[data-purchase-row]');
      if (row) updateRow(row);
    });

    matrix.addEventListener('change', (event) => {
      if (!event.target.matches('[data-row-quantity]')) return;
      const row = event.target.closest('[data-purchase-row]');
      if (row) updateRow(row);
    });

    matrix.addEventListener('click', (event) => {
      const choice = event.target.closest('[data-variant-choice]');
      const row = event.target.closest('[data-purchase-row]');

      if (event.target.closest('[data-variant-picker-toggle]')) {
        const shouldOpen = pickerMenu.hidden;
        if (shouldOpen) {
          openPicker();
        } else {
          closePicker();
        }
        return;
      }
      if (choice) {
        addVariant(choice);
        return;
      }
      if (event.target.closest('[data-quantity-minus]') && row) {
        const input = row.querySelector('[data-row-quantity]');
        input.value = String(Math.max(1, Number(input.value || 1) - 1));
        updateRow(row);
        return;
      }
      if (event.target.closest('[data-quantity-plus]') && row) {
        const input = row.querySelector('[data-row-quantity]');
        input.value = String(Number(input.value || 0) + 1);
        updateRow(row);
        return;
      }
      if (event.target.closest('[data-remove-purchase-row]') && row) {
        removeRow(row);
        return;
      }
      if (event.target.closest('[data-matrix-add-cart]')) {
        submitItems(false);
        return;
      }
      if (event.target.closest('[data-matrix-buy-now]') && checkoutEnabled) {
        submitItems(true);
      }
    });

    document.addEventListener('click', (event) => {
      if (!picker.contains(event.target)) closePicker();
    });

    window.addEventListener('resize', schedulePickerPosition);
    window.addEventListener('scroll', schedulePickerPosition, true);
    window.visualViewport?.addEventListener('resize', schedulePickerPosition);
    window.visualViewport?.addEventListener('scroll', schedulePickerPosition);

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closePicker();
        pickerToggle.focus();
      }
    });
  }
}

if (!customElements.get('product-experience')) {
  customElements.define('product-experience', ProductExperience);
}
